import { useFormContext, useWatch } from "react-hook-form";
import { useMemo } from "react";
import { X, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  type WeeklyTimeEntryFormData,
  TimesheetEntryBaseWageType,
  formatWageType,
  calculateDurationHours,
  formatDuration,
  getDateInTimezone,
  getTimeInTimezone,
  combineDateTimeInTimezone,
  getAvailableDates,
} from "../utils/time-entry-schema";

interface TimeBlockInputProps {
  shiftIndex: number;
  blockIndex: number;
  isFirst: boolean;
  onRemove: () => void;
  shiftDate: Date; // The scheduled shift start date
  timezoneId: string; // IANA timezone of the associated job offer
  // Wage types the shift pays overtime for; the server refuses overtime for any other.
  overtimeWageTypes: TimesheetEntryBaseWageType[];
  allowedBaseWageTypes?: TimesheetEntryBaseWageType[];
}

export function TimeBlockInput({
  shiftIndex,
  blockIndex,
  isFirst,
  onRemove,
  shiftDate,
  timezoneId,
  overtimeWageTypes,
  allowedBaseWageTypes,
}: TimeBlockInputProps) {
  const { control, setValue } = useFormContext<WeeklyTimeEntryFormData>();
  const baseName = `shifts.${shiftIndex}.timeBlocks.${blockIndex}` as const;

  // Watch ISO datetime strings and overtime status
  const startsAt = useWatch({ control, name: `${baseName}.startsAt` });
  const endsAt = useWatch({ control, name: `${baseName}.endsAt` });
  const isOvertime = useWatch({ control, name: `${baseName}.isOvertime` });
  const baseWageType = useWatch({ control, name: `${baseName}.baseWageType` });
  const allowsOvertime = overtimeWageTypes.includes(baseWageType);
  const duration = calculateDurationHours(startsAt || "", endsAt || "");
  const wageTypeOptions = useMemo(
    () => allowedBaseWageTypes ?? Object.values(TimesheetEntryBaseWageType),
    [allowedBaseWageTypes],
  );

  // Providers sometimes begin shortly before a midnight shift. Include the
  // prior calendar day as well as the shift day and two following days.
  const availableDates = useMemo(
    () =>
      getAvailableDates({
        startDate: shiftDate,
        timezoneId,
        daysBefore: 1,
        daysAfter: 2,
      }),
    [shiftDate, timezoneId],
  );

  // Get the default calendar date in the job offer's timezone.
  const defaultDateStr = useMemo(
    () => getDateInTimezone(shiftDate, timezoneId),
    [shiftDate, timezoneId],
  );

  // Helper to update datetime when date or time changes
  const updateDateTime = (
    field: "startsAt" | "endsAt",
    currentValue: string,
    newDate?: string,
    newTime?: string,
  ) => {
    const currentDateStr = currentValue
      ? getDateInTimezone(currentValue, timezoneId)
      : defaultDateStr;
    const currentTimeStr = currentValue
      ? getTimeInTimezone(currentValue, timezoneId)
      : "00:00";

    const dateToUse = newDate || currentDateStr;
    const timeToUse = newTime || currentTimeStr;

    const newValue = combineDateTimeInTimezone(
      dateToUse,
      timeToUse,
      timezoneId,
    );
    setValue(`${baseName}.${field}`, newValue);
  };

  return (
    <div
      className={cn(
        "rounded-lg",
        isFirst ? "bg-white" : "p-2 bg-blue-50/50 border border-blue-200",
      )}
    >
      {/* Header for additional blocks */}
      {!isFirst && (
        <div className="flex items-center justify-between mb-3">
          {isOvertime ? (
            <Badge
              variant="outline"
              className="text-xs bg-blue-100 text-blue-700 border-blue-200"
            >
              Overtime
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-xs bg-slate-100 text-slate-600 border-slate-200"
            >
              Additional Time
            </Badge>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
            onClick={onRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Time inputs - stack on mobile, row on desktop */}
      <div className="space-y-3 mb-3">
        {/* Start */}
        <FormField
          control={control}
          name={`${baseName}.startsAt`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-slate-500">Start</FormLabel>
              <div className="flex gap-2">
                <Select
                  value={
                    field.value
                      ? getDateInTimezone(field.value, timezoneId)
                      : defaultDateStr
                  }
                  onValueChange={(date) =>
                    updateDateTime("startsAt", field.value, date)
                  }
                >
                  <SelectTrigger className="h-10 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDates.map((date) => (
                      <SelectItem key={date.value} value={date.value}>
                        {date.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormControl>
                  <Input
                    type="time"
                    value={
                      field.value
                        ? getTimeInTimezone(field.value, timezoneId)
                        : ""
                    }
                    onChange={(e) =>
                      updateDateTime(
                        "startsAt",
                        field.value,
                        undefined,
                        e.target.value,
                      )
                    }
                    className="h-10 py-1.5 w-[140px]"
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* End */}
        <FormField
          control={control}
          name={`${baseName}.endsAt`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-slate-500">End</FormLabel>
              <div className="flex gap-2">
                <Select
                  value={
                    field.value
                      ? getDateInTimezone(field.value, timezoneId)
                      : defaultDateStr
                  }
                  onValueChange={(date) =>
                    updateDateTime("endsAt", field.value, date)
                  }
                >
                  <SelectTrigger className="h-10 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDates.map((date) => (
                      <SelectItem key={date.value} value={date.value}>
                        {date.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormControl>
                  <Input
                    type="time"
                    value={
                      field.value
                        ? getTimeInTimezone(field.value, timezoneId)
                        : ""
                    }
                    onChange={(e) =>
                      updateDateTime(
                        "endsAt",
                        field.value,
                        undefined,
                        e.target.value,
                      )
                    }
                    className="h-10 py-1.5 w-[140px]"
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Wage type and duration row */}
      <div className="grid grid-cols-2 gap-3">
        <FormField
          control={control}
          name={`${baseName}.baseWageType`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-slate-500 flex items-center gap-1">
                Wage Type
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 text-sm" align="start">
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">
                          Wage Types
                        </h4>
                        <ul className="space-y-1.5 text-slate-600">
                          <li>
                            <span className="font-medium">Regular:</span>{" "}
                            Standard hourly wage
                          </li>
                          <li>
                            <span className="font-medium">Weekend:</span>{" "}
                            Saturday/Sunday shifts
                          </li>
                          <li>
                            <span className="font-medium">Holiday:</span>{" "}
                            Recognized holiday shifts
                          </li>
                          <li>
                            <span className="font-medium">On Call:</span>{" "}
                            Standby availability time
                          </li>
                          <li>
                            <span className="font-medium">
                              On Call Callback:
                            </span>{" "}
                            Called in while on call
                          </li>
                        </ul>
                      </div>
                      <div className="border-t pt-3">
                        <h4 className="font-semibold text-slate-900 mb-1">
                          Adding Overtime
                        </h4>
                        <p className="text-slate-600">
                          To log overtime, add a time block, select the wage
                          type based on the day, then check the "Is Overtime"
                          checkbox.
                        </p>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  if (!overtimeWageTypes.some((type) => type === value)) {
                    setValue(`${baseName}.isOvertime`, false);
                  }
                }}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Select wage type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {wageTypeOptions.map((type) => (
                    <SelectItem key={type} value={type}>
                      {formatWageType(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div>
          <div className="text-xs text-slate-500 mb-1">Duration</div>
          <div className="h-10 flex items-center px-3 bg-slate-50 rounded-md border border-slate-200">
            <span className="font-semibold text-slate-700">
              {duration > 0 ? formatDuration(duration) : "--"}
            </span>
          </div>
        </div>
      </div>

      {/* Overtime checkbox (hidden for first block) */}
      {allowsOvertime && !isFirst && (
        <FormField
          control={control}
          name={`${baseName}.isOvertime`}
          render={({ field }) => (
            <FormItem className="flex items-center gap-2 mt-3">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="text-sm font-normal text-slate-600 cursor-pointer">
                Is Overtime
              </FormLabel>
            </FormItem>
          )}
        />
      )}

      {/* Note input */}
      <FormField
        control={control}
        name={`${baseName}.providerNote`}
        render={({ field }) => (
          <FormItem className="mt-3">
            <FormControl>
              <Input
                {...field}
                placeholder="Add a note (optional)"
                className="text-sm h-9 py-1.5"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
